import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  ArrowLeft, Save, Loader2, AlertTriangle, CheckCircle2, Info, X, Plus, Sparkles, ExternalLink, RefreshCw, Lock,
} from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";
import { apiClient } from "../lib/api";
import { SalesEvidence } from "../components/analysis/SalesEvidence";

// Liste paketi duzenleyici — Etsy ilan sayfasi duzeninde. Solda galeri ve
// canli kisisellestirme onizlemesi (alicinin yazacagi deger aninda basilir),
// sagda satin alma kutusu (baslik, fiyat, secenekler, kisisellestirme),
// altta aciklama, etiketler, ornek degerler, kontroller ve ilham kaniti.
// Taslak olustuktan sonra metin Listing Builder'da duzenlenir.

interface PersoField {
  fieldKey: string; label: string; type?: "text_input" | "dropdown"; required?: boolean;
  instructions?: string; maxChar?: number; options?: string[]; example?: string;
}
interface SampleRender { url: string; backUrl?: string | null; darkUrl?: string | null; values: Record<string, string> }
interface Check { level: "blocker" | "review" | "info"; code: string; message: string }
interface PackageDetail {
  designId: string; name: string; sku: string | null; status: string; statusTr: string; imageUrl: string;
  shop: string | null; template: { id: string; name: string } | null;
  draft: { id: string; title: string; status: string; etsyListingId: string | null } | null;
  checks: Check[];
  package: {
    printSide: string | null; samples: Array<Record<string, string>>; aiGenerated: boolean;
    members: Array<{ designId: string; label: string }>; memberOf: string | null; memberLabel: string | null;
    template: { version: number; layerCount: number; layers: Array<{ fieldKey: string; label?: string }> } | null;
    sampleRenders: SampleRender[]; sellingMockupSuggestion: string | null; inspirationSuggestion: string | null;
  };
  seo: { title?: string; description?: string; tags?: string[] } | null;
  personalization: PersoField[]; isCustom: boolean; analysisId: string | null;
}
interface TemplateRow { id: string; name: string; price?: number | null; variations?: Array<{ name?: string; options?: Array<{ label?: string; sellingPrice?: number }> }> }

const TITLE_MAX = 140, TAG_MAX = 13, TAG_CHARS = 20;
const abs = (u: string) => (/^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u.startsWith("/") ? "" : "/"}${u}`);

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api${path}`, { method, headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

const STATUS_TONE: Record<string, string> = {
  awaiting_mockup: "bg-violet-100 text-violet-800", building: "bg-indigo-100 text-indigo-800", build_error: "bg-red-100 text-red-800",
  needs_review: "bg-amber-100 text-amber-800", ready_in_panel: "bg-emerald-100 text-emerald-800",
  etsy_draft: "bg-sky-100 text-sky-800", published: "bg-green-100 text-green-800",
};

export default function PackageEditorPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { data: pkg, isLoading, error } = useQuery({ queryKey: ["package", id], queryFn: () => api<PackageDetail>("GET", `/packages/${id}`) });
  const { data: templates = [] } = useQuery({ queryKey: ["listing-templates"], queryFn: () => api<TemplateRow[]>("GET", "/listing-templates") });
  const { data: analysis } = useQuery({
    queryKey: ["analysis", pkg?.analysisId], enabled: Boolean(pkg?.analysisId),
    queryFn: () => apiClient.getAnalysis(pkg!.analysisId!),
  });

  // ---- Form durumu (paket yuklenince doldurulur) ----
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [perso, setPerso] = useState<PersoField[]>([]);
  const [templateId, setTemplateId] = useState<string>("");
  const [samples, setSamples] = useState<Array<Record<string, string>>>([]);
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!pkg || loadedFor.current === pkg.designId) return;
    loadedFor.current = pkg.designId;
    setTitle(pkg.seo?.title ?? "");
    setDescription(pkg.seo?.description ?? "");
    setTags(pkg.seo?.tags ?? []);
    setPerso(pkg.personalization ?? []);
    setTemplateId(pkg.template?.id ?? "");
    setSamples(pkg.package.samples ?? []);
  }, [pkg]);

  const locked = Boolean(pkg?.draft);
  const dirty = Boolean(pkg) && (
    title !== (pkg!.seo?.title ?? "") || description !== (pkg!.seo?.description ?? "")
    || JSON.stringify(tags) !== JSON.stringify(pkg!.seo?.tags ?? [])
    || JSON.stringify(perso) !== JSON.stringify(pkg!.personalization ?? [])
    || templateId !== (pkg!.template?.id ?? "")
  );

  const save = useMutation({
    mutationFn: () => api<PackageDetail>("PATCH", `/packages/${id}`, { title, description, tags, personalization: perso, listingTemplateId: templateId || null }),
    onSuccess: (d) => {
      qc.setQueryData(["package", id], d);
      setPerso(d.personalization ?? []);
      setMsg({ tone: "ok", text: "Kaydedildi" });
      void qc.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (e) => setMsg({ tone: "err", text: e instanceof Error ? e.message : String(e) }),
  });
  const saveSamples = useMutation({
    mutationFn: () => api<{ ok: boolean }>("PATCH", `/packages/${id}/template`, { samples }),
    onSuccess: () => { setMsg({ tone: "ok", text: "Örnekler kaydedildi, yeniden basıldı" }); void qc.invalidateQueries({ queryKey: ["package", id] }); },
    onError: (e) => setMsg({ tone: "err", text: e instanceof Error ? e.message : String(e) }),
  });
  useEffect(() => {
    if (msg?.tone !== "ok") return undefined;
    const t = setTimeout(() => setMsg(null), 3000);
    return () => clearTimeout(t);
  }, [msg]);

  // ---- Canli kisisellestirme onizlemesi ----
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!pkg) return;
    const first = pkg.package.samples?.[0] ?? pkg.package.sampleRenders?.[0]?.values ?? {};
    setValues(Object.fromEntries((pkg.personalization ?? []).map((p) => [p.fieldKey, first[p.fieldKey] ?? p.example ?? ""])));
  }, [pkg?.designId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selected, setSelected] = useState<string>("");
  const [preview, setPreview] = useState<{ url: string; backUrl?: string | null } | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const hasTemplate = Boolean(pkg?.package.template);
  useEffect(() => {
    if (!hasTemplate || !Object.keys(values).length) return;
    const t = setTimeout(async () => {
      setPreviewBusy(true); setPreviewErr(null);
      try {
        const r = await api<{ frontUrl: string; backUrl?: string | null }>("POST", `/packages/${id}/render`, { values, preview: true });
        setPreview({ url: r.frontUrl, backUrl: r.backUrl });
        setSelected("preview");
      } catch (e) { setPreviewErr(e instanceof Error ? e.message : String(e)); }
      finally { setPreviewBusy(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [values, hasTemplate, id]);

  // ---- Galeri ----
  type G = { key: string; url: string; label: string; dark?: boolean };
  const gallery: G[] = useMemo(() => {
    if (!pkg) return [];
    const out: G[] = [];
    if (preview) out.push({ key: "preview", url: preview.url, label: "Canlı önizleme" });
    if (preview?.backUrl) out.push({ key: "preview-back", url: preview.backUrl, label: "Önizleme — arka" });
    pkg.package.sampleRenders.forEach((s, i) => {
      const name = Object.values(s.values).filter(Boolean).join(" · ");
      out.push({ key: `s${i}`, url: s.url, label: name || `Örnek ${i + 1}` });
      if (s.darkUrl) out.push({ key: `s${i}d`, url: s.darkUrl, label: `${name || `Örnek ${i + 1}`} — koyu`, dark: true });
      if (s.backUrl) out.push({ key: `s${i}b`, url: s.backUrl, label: `${name || `Örnek ${i + 1}`} — arka` });
    });
    out.push({ key: "art", url: pkg.imageUrl, label: "Sabit grafik" });
    return out;
  }, [pkg, preview]);
  const current = gallery.find((g) => g.key === selected) ?? gallery[0];
  const [bg, setBg] = useState<"light" | "dark" | "check">("light");
  useEffect(() => { if (current?.dark) setBg("dark"); }, [current?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Sablon: fiyat ve secenekler ----
  const tpl = templates.find((t) => t.id === templateId);
  const priceText = useMemo(() => {
    const ps = (tpl?.variations ?? []).flatMap((v) => (v.options ?? []).map((o) => o.sellingPrice)).filter((p): p is number => typeof p === "number");
    if (ps.length) { const lo = Math.min(...ps), hi = Math.max(...ps); return lo === hi ? `$${lo.toFixed(2)}` : `$${lo.toFixed(2)} – $${hi.toFixed(2)}`; }
    return tpl?.price ? `$${tpl.price.toFixed(2)}` : "Fiyat şablondan gelir";
  }, [tpl]);

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (error || !pkg) return <div className="p-8 text-sm text-red-600">{error instanceof Error ? error.message : "Paket bulunamadı"}</div>;

  const blockers = pkg.checks.filter((c) => c.level === "blocker");
  const reviews = pkg.checks.filter((c) => c.level === "review");
  const wordCount = title.trim() ? title.trim().split(/\s+/).length : 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
      {/* Ust cubuk */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Link to="/listing-review" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" /> Liste Denetimi
        </Link>
        <span className="text-gray-300">/</span>
        <span className="font-mono text-sm text-gray-600">{pkg.sku ?? pkg.name}</span>
        <span className={clsx("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_TONE[pkg.status] ?? "bg-gray-100 text-gray-700")}>{pkg.statusTr}</span>
        {pkg.isCustom && <span className="rounded-full bg-pink-100 px-2.5 py-0.5 text-xs font-semibold text-pink-700">Custom</span>}
        {pkg.shop && <span className="text-xs text-gray-400">{pkg.shop}</span>}
        <div className="ml-auto flex items-center gap-2">
          {msg && <span className={clsx("text-sm", msg.tone === "ok" ? "text-emerald-600" : "text-red-600")}>{msg.text}</span>}
          {locked && pkg.draft ? (
            <Link to={`/listing-builder?id=${pkg.draft.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black">
              Taslağı düzenle <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          ) : (
            <button onClick={() => save.mutate()} disabled={!dirty || save.isPending}
              className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-5 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-40">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
            </button>
          )}
        </div>
      </div>

      {locked && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <Lock className="h-4 w-4" /> Bu paketin taslağı oluştu; başlık, açıklama ve etiketler artık taslakta düzenlenir. Burası salt okunur önizleme.
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-12">
        {/* ===== Galeri ===== */}
        <div className="lg:col-span-7">
          <div className="flex gap-3">
            <div className="flex max-h-[600px] w-16 shrink-0 flex-col gap-2 overflow-y-auto">
              {gallery.map((g) => (
                <button key={g.key} onClick={() => setSelected(g.key)} title={g.label}
                  className={clsx("aspect-square w-16 overflow-hidden rounded-lg border-2 bg-gray-50", current?.key === g.key ? "border-gray-900" : "border-transparent hover:border-gray-300")}>
                  <img src={abs(g.url)} alt="" className={clsx("h-full w-full object-contain", g.dark && "bg-gray-800")} />
                </button>
              ))}
            </div>
            <div className="relative flex-1">
              <div className={clsx("flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-gray-200",
                bg === "light" && "bg-white", bg === "dark" && "bg-gray-800",
                bg === "check" && "bg-[repeating-conic-gradient(#f3f4f6_0_25%,#fff_0_50%)] bg-[length:20px_20px]")}>
                {current && <img src={abs(current.url)} alt={current.label} className="max-h-full max-w-full object-contain p-6" />}
                {previewBusy && current?.key === "preview" && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/40"><Loader2 className="h-6 w-6 animate-spin text-gray-500" /></div>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span className="truncate">{current?.label}</span>
                <div className="ml-auto flex overflow-hidden rounded-full border border-gray-200">
                  {(["light", "dark", "check"] as const).map((b) => (
                    <button key={b} onClick={() => setBg(b)} className={clsx("px-2.5 py-1", bg === b ? "bg-gray-900 text-white" : "hover:bg-gray-50")}>
                      {b === "light" ? "Açık ürün" : b === "dark" ? "Koyu ürün" : "Şeffaf"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {!hasTemplate && pkg.isCustom && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Bu paketin baskı şablonu yok — canlı önizleme ve örnek isim basımı için ChatGPT'den şablon kurmasını iste (build_package_template).</p>
          )}
          {previewErr && <p className="mt-2 text-xs text-red-600">Önizleme: {previewErr}</p>}
        </div>

        {/* ===== Satin alma kutusu ===== */}
        <div className="lg:col-span-5">
          <p className="mb-1 text-sm text-gray-500">{pkg.shop ?? "Mağaza"}</p>
          <textarea value={title} onChange={(e) => setTitle(e.target.value)} disabled={locked} rows={3}
            className="w-full resize-none rounded-lg border border-transparent px-0 text-[22px] font-normal leading-snug text-gray-900 hover:border-gray-200 focus:border-gray-300 focus:px-2 focus:outline-none disabled:bg-transparent" />
          <div className="mb-3 flex gap-3 text-[11px]">
            <span className={clsx(title.length > TITLE_MAX ? "text-red-600" : "text-gray-400")}>{title.length}/{TITLE_MAX}</span>
            <span className={clsx(wordCount > 15 ? "text-amber-600" : "text-gray-400")}>{wordCount} kelime</span>
          </div>
          <p className="mb-4 text-2xl font-semibold text-gray-900">{priceText}</p>

          <label className="mb-1 block text-xs font-medium text-gray-500">Ürün şablonu</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={locked}
            className="mb-4 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-gray-900 focus:outline-none">
            <option value="">— Şablon seç —</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          {(tpl?.variations ?? []).map((v) => (
            <div key={v.name} className="mb-3">
              <label className="mb-1 block text-sm font-medium text-gray-900">{v.name} <span className="font-normal text-gray-400">({v.options?.length ?? 0})</span></label>
              <select className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-600" defaultValue="">
                <option value="">Bir seçenek seç</option>
                {(v.options ?? []).map((o) => <option key={o.label}>{o.label}{o.sellingPrice != null ? ` ($${o.sellingPrice.toFixed(2)})` : ""}</option>)}
              </select>
            </div>
          ))}

          {/* Kisisellestirme — Etsy kutusu */}
          {perso.length > 0 && (
            <div className="mt-4 rounded-2xl border border-gray-200 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-pink-500" />
                <h3 className="text-sm font-semibold text-gray-900">Kişiselleştirme</h3>
                <span className="ml-auto text-[11px] text-gray-400">Alıcı alanına yaz → önizleme basılır</span>
              </div>
              <div className="space-y-4">
                {perso.map((q, i) => {
                  const upd = (patch: Partial<PersoField>) => setPerso((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
                  const val = values[q.fieldKey] ?? "";
                  return (
                    <div key={q.fieldKey} className="rounded-xl bg-gray-50 p-3">
                      <div className="flex items-center gap-2">
                        <input value={q.label} onChange={(e) => upd({ label: e.target.value })} disabled={locked} maxLength={45}
                          className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 text-sm font-semibold text-gray-900 hover:border-gray-200 focus:border-gray-300 focus:bg-white focus:outline-none" />
                        <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-gray-400" title="Baskı katmanı anahtarı">{q.fieldKey}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <label className="flex items-center gap-1"><input type="checkbox" checked={Boolean(q.required)} disabled={locked} onChange={(e) => upd({ required: e.target.checked })} /> Zorunlu</label>
                        {q.type !== "dropdown" && (
                          <label className="flex items-center gap-1">En fazla
                            <input type="number" min={1} max={256} value={q.maxChar ?? ""} disabled={locked}
                              onChange={(e) => upd({ maxChar: e.target.value ? Number(e.target.value) : undefined })}
                              className="w-14 rounded border border-gray-200 px-1 py-0.5" /> karakter
                          </label>
                        )}
                        {q.type === "dropdown" && <span className="rounded bg-white px-1.5 py-0.5">Açılır liste</span>}
                      </div>
                      <input value={q.instructions ?? ""} onChange={(e) => upd({ instructions: e.target.value })} disabled={locked} maxLength={120}
                        placeholder="Alıcıya talimat (ör. Ex: Emma)"
                        className="mt-2 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:border-gray-400 focus:outline-none" />
                      {q.type === "dropdown" ? (
                        <>
                          <input value={(q.options ?? []).join(", ")} disabled={locked}
                            onChange={(e) => upd({ options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                            placeholder="Seçenekler, virgülle"
                            className="mt-2 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs" />
                          <select value={val} onChange={(e) => setValues((v) => ({ ...v, [q.fieldKey]: e.target.value }))}
                            className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm">
                            <option value="">Alıcı seçimi…</option>
                            {(q.options ?? []).map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </>
                      ) : (
                        <div className="relative mt-2">
                          <input value={val} onChange={(e) => setValues((v) => ({ ...v, [q.fieldKey]: e.target.value }))}
                            placeholder="Alıcının yazacağı değer"
                            className={clsx("w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none",
                              q.maxChar && val.length > q.maxChar ? "border-red-400" : "border-gray-300 focus:border-gray-900")} />
                          {q.maxChar ? <span className="absolute right-2 top-2.5 text-[10px] text-gray-400">{val.length}/{q.maxChar}</span> : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {pkg.package.members.length > 0 && (
            <p className="mt-3 text-xs text-gray-500">Birden fazla tasarım: Design 1 + {pkg.package.members.map((m) => m.label).join(", ")}</p>
          )}
          {pkg.package.sellingMockupSuggestion && (
            <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600"><span className="font-medium">Mockup önerisi:</span> {pkg.package.sellingMockupSuggestion}</p>
          )}
        </div>
      </div>

      {/* ===== Alt bolum ===== */}
      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-7">
          <section>
            <h2 className="mb-2 text-lg font-semibold text-gray-900">Açıklama</h2>
            <div className="mb-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              <span className="font-medium text-gray-600">Arama önizlemesi (ilk 160): </span>{description.slice(0, 160) || "—"}
            </div>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={locked} rows={16}
              className="w-full rounded-xl border border-gray-300 p-4 text-sm leading-relaxed text-gray-800 focus:border-gray-900 focus:outline-none" />
            <p className="mt-1 text-[11px] text-gray-400">{description.length} karakter</p>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold text-gray-900">Etiketler <span className="text-sm font-normal text-gray-400">{tags.length}/{TAG_MAX}</span></h2>
            <TagEditor tags={tags} onChange={setTags} disabled={locked} />
          </section>

          {hasTemplate && pkg.personalization.length > 0 && (
            <section>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Örnek değerler</h2>
                <span className="text-xs text-gray-400">Mockuplarda basılan isimler — en uzun izinli değeri de dene</span>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500">
                    <tr>{pkg.personalization.map((p) => <th key={p.fieldKey} className="px-3 py-2 text-left font-medium">{p.label}</th>)}<th className="w-8" /></tr>
                  </thead>
                  <tbody>
                    {samples.map((s, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        {pkg.personalization.map((p) => (
                          <td key={p.fieldKey} className="px-2 py-1">
                            <input value={s[p.fieldKey] ?? ""} maxLength={60}
                              onChange={(e) => setSamples((ss) => ss.map((x, j) => (j === i ? { ...x, [p.fieldKey]: e.target.value } : x)))}
                              className={clsx("w-full rounded border px-2 py-1", p.maxChar && (s[p.fieldKey] ?? "").length > p.maxChar ? "border-red-400" : "border-gray-200")} />
                          </td>
                        ))}
                        <td className="px-1"><button onClick={() => setSamples((ss) => ss.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex gap-2">
                {samples.length < 5 && (
                  <button onClick={() => setSamples((ss) => [...ss, Object.fromEntries(pkg.personalization.map((p) => [p.fieldKey, ""]))])}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50"><Plus className="h-3.5 w-3.5" /> Örnek ekle</button>
                )}
                <button onClick={() => saveSamples.mutate()} disabled={saveSamples.isPending || JSON.stringify(samples) === JSON.stringify(pkg.package.samples ?? [])}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40">
                  {saveSamples.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Kaydet ve yeniden bas
                </button>
              </div>
            </section>
          )}
        </div>

        <div className="space-y-6 lg:col-span-5">
          <section className="rounded-2xl border border-gray-200 p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Kontroller</h2>
            {pkg.checks.length === 0 ? (
              <p className="flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Sorun yok</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {[...blockers, ...reviews, ...pkg.checks.filter((c) => c.level === "info")].map((c, i) => (
                  <li key={i} className="flex gap-2">
                    {c.level === "blocker" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                      : c.level === "review" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                      : <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />}
                    <span className={clsx(c.level === "blocker" ? "text-red-700" : c.level === "review" ? "text-amber-800" : "text-gray-600")}>{c.message}</span>
                  </li>
                ))}
              </ul>
            )}
            {dirty && !locked && <p className="mt-3 text-[11px] text-gray-400">Kontroller kaydettikten sonra yenilenir.</p>}
          </section>

          {analysis ? (
            <div className="space-y-2">
              <Link to={`/analyses/${analysis.id}`} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
                İlham: {analysis.source?.title?.slice(0, 60) ?? "analiz"} <ExternalLink className="h-3.5 w-3.5" />
              </Link>
              {analysis.research ? <SalesEvidence research={analysis.research} /> : null}
            </div>
          ) : pkg.package.inspirationSuggestion ? (
            <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600"><span className="font-medium">İlham önerisi:</span> {pkg.package.inspirationSuggestion}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TagEditor({ tags, onChange, disabled }: { tags: string[]; onChange: (t: string[]) => void; disabled?: boolean }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const parts = draft.split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...tags];
    for (const p of parts) if (next.length < TAG_MAX && !next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next); setDraft("");
  };
  return (
    <div className="rounded-xl border border-gray-300 p-3">
      <div className="flex flex-wrap gap-2">
        {tags.map((t, i) => (
          <span key={`${t}-${i}`} className={clsx("inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm",
            t.length > TAG_CHARS ? "bg-red-50 text-red-700 ring-1 ring-red-200" : "bg-gray-100 text-gray-800")}>
            {t}<span className="text-[10px] text-gray-400">{t.length}</span>
            {!disabled && <button onClick={() => onChange(tags.filter((_, j) => j !== i))} className="text-gray-400 hover:text-gray-700"><X className="h-3.5 w-3.5" /></button>}
          </span>
        ))}
      </div>
      {!disabled && tags.length < TAG_MAX && (
        <input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={60}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add}
          placeholder="Etiket ekle (Enter) — en fazla 20 karakter"
          className="mt-2 w-full border-0 px-1 text-sm focus:outline-none" />
      )}
    </div>
  );
}
